/**
 * Error Handler Utility
 * Provides helper functions for handling API errors
 */

import { ApiError } from '../types';

/**
 * Format error message for display
 */
export const formatErrorMessage = (error: ApiError): string => {
  if (error.errors && Object.keys(error.errors).length > 0) {
    // Format validation errors
    const errorMessages = Object.entries(error.errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('\n');
    return errorMessages;
  }
  
  return error.message || 'An unexpected error occurred';
};

/**
 * Check if error is a network error
 */
export const isNetworkError = (error: ApiError): boolean => {
  return error.code === 'NETWORK_ERROR' || error.status === 0;
};

/**
 * Check if error is an authentication error
 */
export const isAuthError = (error: ApiError): boolean => {
  return error.status === 401 || error.status === 403;
};

/**
 * Check if error is a validation error
 */
export const isValidationError = (error: ApiError): boolean => {
  return error.status === 422 || !!(error.errors && Object.keys(error.errors).length > 0);
};

/**
 * Check if error is a not found error
 */
export const isNotFoundError = (error: ApiError): boolean => {
  return error.status === 404;
};

/**
 * Check if error is a server error
 */
export const isServerError = (error: ApiError): boolean => {
  return error.status !== undefined && error.status >= 500;
};

/**
 * Get user-friendly error message
 */
export const getUserFriendlyErrorMessage = (error: ApiError): string => {
  if (isNetworkError(error)) {
    return 'Bağlantı hatası. Lütfen internet bağlantınızı kontrol edin.';
  }
  
  if (isAuthError(error)) {
    return 'Oturum süreniz doldu. Lütfen tekrar giriş yapın.';
  }
  
  if (isNotFoundError(error)) {
    return 'Aradığınız kayıt bulunamadı.';
  }
  
  if (isServerError(error)) {
    return 'Sunucu hatası. Lütfen daha sonra tekrar deneyin.';
  }
  
  if (isValidationError(error)) {
    return formatErrorMessage(error);
  }
  
  return error.message || 'Beklenmeyen bir hata oluştu.';
};

/**
 * Log error for debugging
 */
export const logError = (error: ApiError, context?: string): void => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[API Error${context ? ` - ${context}` : ''}]:`, {
      message: error.message,
      code: error.code,
      status: error.status,
      errors: error.errors,
    });
  }
};

/**
 * Handle error with toast notification
 * (Requires toast library to be implemented)
 */
export const handleErrorWithToast = (
  error: ApiError,
  showToast?: (message: string, type: 'error' | 'warning' | 'info') => void
): void => {
  const message = getUserFriendlyErrorMessage(error);
  
  if (showToast) {
    const type = isServerError(error) || isNetworkError(error) ? 'error' : 'warning';
    showToast(message, type);
  } else {
    // Fallback to console if toast is not available
    console.error(message);
  }
  
  logError(error);
};

